
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from './api';

interface User {
  id: string;
  role: string;
  phone?: string;
  firstName: string;
  lastName: string;
  email?: string;
  emailVerified?: boolean;
  loyaltyPoints?: number;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  login: (identifier: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (input: { phone: string; password: string; firstName: string; lastName: string; email?: string }) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  refreshSession: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      login: async (identifier, password) => {
        const result = await api.login({ identifier, password });
        if (result.success && result.data) {
          const { accessToken, refreshToken, user } = result.data;
          api.setToken(accessToken);
          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
          });
          return { success: true };
        }
        return { success: false, error: result.error?.message ?? 'Login failed' };
      },

      signup: async (input) => {
        const result = await api.signup(input);
        if (result.success && result.data) {
          const { accessToken, refreshToken, user } = result.data;
          api.setToken(accessToken);
          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
          });
          return { success: true };
        }
        return { success: false, error: result.error?.message ?? 'Signup failed' };
      },

      logout: () => {
        api.logout().catch(() => {});
        api.setToken(null);
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      setTokens: (accessToken, refreshToken) => {
        api.setToken(accessToken);
        set({ accessToken, refreshToken, isAuthenticated: true });
      },

      setUser: (user) => set({ user }),

      refreshSession: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return false;

        const result = await api.refreshToken(refreshToken);
        if (result.success && result.data) {
          api.setToken(result.data.accessToken);
          set({
            accessToken: result.data.accessToken,
            refreshToken: result.data.refreshToken,
          });
          return true;
        }

        // Refresh failed — log out
        get().logout();
        return false;
      },
    }),
    {
      name: 'cleanyarn-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Restore API token on hydrate
        if (state?.accessToken) {
          api.setToken(state.accessToken);
        }
      },
    }
  )
);
